import React, { FC, useState } from 'react';
import { Tabs, useTheme } from '@zydalabs/storefront-components';
import useTranslation from 'next-translate/useTranslation';
import { DateTime } from 'luxon';

import IcOutArrow from 'common/icons/IcOutArrow';
import IcInArrow from 'common/icons/IcInArrow';
import { URLS } from 'common/constants';
import { EmptyState, InfoBlock } from 'common/components';
import { changeRoute } from 'common/utils';
import { WalletTransaction } from 'service/types';
import { TRANSACTION_TYPES } from 'modules/userModule/enums';

type WalletTabsType = {
  walletTransactions: Array<WalletTransaction>;
  walletBalance: number;
};

const WalletTabs: FC<WalletTabsType> = ({ walletTransactions, walletBalance }) => {
  const [tabIndex, setTabIndex] = useState('1');
  const { t } = useTranslation('wallet');
  const theme = useTheme();

  const getWalletTabsComponents = (walletTransactionType?: string) =>
    walletTransactions?.length > 0 ? (
      getInfoBlocks(walletTransactionType)
    ) : (
      <EmptyState title={t('noCreditMessage')} halfHeight />
    );

  const getInfoBlocks = (walletTransactionType?: string) => {
    let transactions = walletTransactions;
    if (walletTransactionType) {
      transactions = walletTransactions?.filter(transaction => transaction.transactionType === walletTransactionType);
    }
    return transactions?.map(transaction => {
      const isOutTransaction = transaction.transactionType === TRANSACTION_TYPES.OUT;
      return (
        <InfoBlock
          startIcon={isOutTransaction ? IcOutArrow : IcInArrow}
          key={transaction.amount}
          title={t(transaction.transactionReason)}
          details={DateTime.fromISO(transaction.createdAt).toLocaleString(DateTime.DATE_MED)}
          isIconLarge
          isDetailsTextSmall
          actionText={transaction.amount.toString()}
          actionTextColor={isOutTransaction ? theme.color.danger : theme.color.success}
          titleColor={theme.color.black}
          onActionClick={() => changeRoute({ pathname: URLS.WALLET_TRANSACTIONS_DETAILS, query: { id: transaction.id } })}
        />
      );
    });
  };

  return (
    <div data-testid="walletTabs-component">
      <Tabs
        tabs={[
          {
            component: getWalletTabsComponents(),
            name: t('all'),
            tabValue: '1',
          },
          {
            component: getWalletTabsComponents(TRANSACTION_TYPES.IN),
            name: t('in'),
            tabValue: '2',
          },
          {
            component: getWalletTabsComponents(TRANSACTION_TYPES.OUT),
            name: t('out'),
            tabValue: '3',
          },
        ]}
        value={tabIndex}
        onChange={(tab: string) => setTabIndex(tab)}
        variant="underlined"
      />
    </div>
  );
};

export default WalletTabs;
